import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Check, Package, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const INPUT =
  "w-full border border-black/15 bg-white px-4 py-3 text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-volt focus:border-volt transition-colors duration-200";

export default function Track() {
  const [params] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(params.get("order") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = async (o = orderNumber, e = email) => {
    if (!o || !e) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/orders/track", { params: { order_number: o, email: e } });
      setResult(data);
    } catch (err) {
      setResult(null);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("order") && params.get("email")) lookup(params.get("order"), params.get("email"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentIdx = result ? result.all_statuses.indexOf(result.status) : -1;

  return (
    <div className="min-h-screen bg-mist" data-testid="track-page">
      <header className="bg-white border-b border-black/5">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-black/60 hover:text-ink transition-colors">
            <ArrowLeft className="w-4 h-4" /> Revora Reviews
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-display text-4xl font-black tracking-tight text-ink">Track your order</h1>
        <p className="text-black/55 text-sm mt-2 mb-10">Enter your order number and the email you ordered with.</p>

        <div className="bg-white border border-black/10 rounded-sm p-6 flex flex-col sm:flex-row gap-3 mb-10">
          <input data-testid="track-order-input" className={INPUT} placeholder="Order number (e.g. RV-AB12CD)" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} />
          <input data-testid="track-email-input" type="email" className={INPUT} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <button
            data-testid="track-submit"
            onClick={() => lookup()}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-ink text-white font-bold px-7 py-3 rounded-full hover:bg-volt disabled:opacity-40 transition-colors duration-300 whitespace-nowrap"
          >
            <Search className="w-4 h-4" /> {loading ? "Checking…" : "Track"}
          </button>
        </div>

        {error && <p data-testid="track-error" className="text-sm text-red-500 mb-6">{error}</p>}

        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-black/10 rounded-sm p-8" data-testid="track-result">
            <div className="flex flex-wrap justify-between gap-4 mb-2">
              <div>
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/40">Order</p>
                <p className="font-display text-2xl font-black text-ink">{result.order_number}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/40">Package</p>
                <p className="font-bold text-ink">{result.quantity} cards — {result.business_name}</p>
              </div>
            </div>
            {currentIdx >= 4 && currentIdx < 6 && (
              <p className="text-voltdark font-bold text-sm mt-2 mb-2 flex items-center gap-2" data-testid="track-on-the-way">
                <Package className="w-4 h-4" /> Your Revora cards are on their way.
              </p>
            )}
            <div className="mt-8 space-y-0" data-testid="track-stepper">
              {result.all_statuses.map((s, i) => {
                const done = i <= currentIdx;
                const current = i === currentIdx;
                return (
                  <div key={s} className="flex gap-4" data-testid={`track-status-${i}`}>
                    <div className="flex flex-col items-center">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition-colors duration-300 ${
                        done ? "bg-volt border-volt" : "border-black/15 bg-white"
                      }`}>
                        {done && <Check className="w-3.5 h-3.5 text-white" />}
                      </span>
                      {i < result.all_statuses.length - 1 && (
                        <span className={`w-0.5 h-8 ${i < currentIdx ? "bg-volt" : "bg-black/10"}`} />
                      )}
                    </div>
                    <div className="pb-8">
                      <p className={`text-sm font-bold ${current ? "text-voltdark" : done ? "text-ink" : "text-black/35"}`}>{s}</p>
                      {current && result.status_history?.find((h) => h.status === s) && (
                        <p className="text-xs text-black/40 mt-0.5">
                          {new Date(result.status_history.find((h) => h.status === s).at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
